import { useState } from 'react';
import { Button, Dialog, Field, Icon, Input, useErrorDialog, useToast } from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';
import type { BrandWriteResult } from './types.ts';

/**
 * File a new brand.
 *
 * The one field to read carefully is the HOST. It is not a display field: the
 * player portal decides which brand a registration belongs to by matching the
 * request's Host header against `platform.brand.domain`, so this string decides
 * whose player somebody becomes, whose licence covers them and whose commission
 * they generate. The dialog says that in as many words, because an operator who
 * treats it as a label will type "FairPlay" into it.
 *
 * Everything checked below is checked AGAIN on the server — @dayon/contracts
 * normalises and validates the host, and the route refuses a collision against
 * `lower(domain)`. These checks exist to answer the operator without a round
 * trip, never as the boundary.
 */

/** Mirrors `brandDomain` in @dayon/contracts, and is not a substitute for it. */
const HOST = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

export function NewBrandDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [licenceRef, setLicenceRef] = useState('');
  /** `<input type="date">` gives a calendar day; the route wants an instant. */
  const [expires, setExpires] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<BrandWriteResult | null>(null);

  const close = () => {
    setName('');
    setDomain('');
    setLicenceRef('');
    setExpires('');
    setFields({});
    setCreated(null);
    onClose();
  };

  const submit = async () => {
    const problems: Record<string, string> = {};
    const host = domain.trim().toLowerCase().replace(/\.$/, '');
    if (name.trim().length < 2) problems['name'] = 'Give the brand a name.';
    if (!HOST.test(host)) {
      problems['domain'] =
        'The bare host only — peryaplay.ph. No https://, no port, no path, and at least one dot.';
    }
    if (licenceRef.trim() !== '' && licenceRef.trim().length < 3) {
      problems['licenceRef'] = 'A licence reference is at least 3 characters, or leave it empty.';
    }
    if (expires !== '' && new Date(`${expires}T00:00:00+08:00`).getTime() <= Date.now()) {
      problems['licenceExpiresAt'] = 'Pick the date the licence runs to — it must be ahead.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<BrandWriteResult>('/api/platform/brands', {
        name: name.trim(),
        domain: host,
        ...(licenceRef.trim() ? { licenceRef: licenceRef.trim() } : {}),
        /* Midnight Manila, sent as an instant. A licence expires at the moment
           the regulator names; the date control can only offer a day, so the
           day is anchored to the gaming day's own clock rather than to the
           browser's — which is the bug that has bitten this repo four times. */
        ...(expires ? { licenceExpiresAt: new Date(`${expires}T00:00:00+08:00`).toISOString() } : {}),
      });
      setCreated(result);
      setFields({});
      toast(`${result.ref} created — ${result.domain} now resolves to it.`);
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not create the brand',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was created. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      wide
      title={created === null ? 'New brand' : `${created.ref} created`}
      actions={
        created === null ? (
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" busy={busy} onClick={() => void submit()}>
              Create brand
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        )
      }
    >
      {created === null ? (
        <div className="stack">
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              The host is a <strong>tenancy boundary</strong>, not a label. The player portal
              resolves which brand a new registration belongs to by matching the browser&rsquo;s
              Host header against it, so it decides whose player somebody becomes and whose licence
              covers them. One host, one brand — the platform refuses a second brand on the same
              host, whatever its capitalisation.
            </div>
          </div>

          <Field label="Brand name" error={fields['name']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="FairPlay"
              />
            )}
          </Field>

          <Field
            label="Host"
            error={fields['domain']}
            hint="The bare host players reach it at — peryaplay.ph. Stored lower case, because that is what the portal matches on."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="peryaplay.ph"
                inputMode="url"
                autoCapitalize="none"
                spellCheck={false}
              />
            )}
          </Field>

          <div className="halves">
            <Field
              label="Licence reference"
              error={fields['licenceRef']}
              hint="Optional. The regulator's own reference, quoted back to them."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={licenceRef}
                  onChange={(e) => setLicenceRef(e.target.value)}
                  placeholder="PAGCOR-2026-0417"
                />
              )}
            </Field>

            <Field
              label="Licence expires (PHT)"
              error={fields['licenceExpiresAt']}
              hint="Optional. Once set, the dashboard warns 60 days out — and this screen is where it is renewed."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="date"
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={expires}
                  onChange={(e) => setExpires(e.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              A new brand starts <strong>Live</strong> with no players, no games and no payment
              methods enabled. Creating it does not point any DNS at this platform — it records that
              the host belongs to this brand, so that a request arriving from it can be resolved.
            </div>
          </div>
        </div>
      ) : (
        <div className="stack">
          <div className="notice">
            <Icon name="check-circle" />
            <div>
              <strong>{created.name}</strong> is live as{' '}
              <span className="tabular">{created.ref}</span>, and{' '}
              <span className="tabular">{created.domain}</span> now resolves to it. It appears in
              the brand selector at the top of every screen.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="list-checks" />
            <div>
              Nothing else has been set up for it yet: no games are enabled, no payment method is
              available, and no agent or affiliate is attributed to it. Those are separate screens,
              and each one will show this brand as an option now that it exists.
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
